import React, { useEffect } from 'react';
import { Container, Box, Typography, Button, Paper } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Confetti from 'react-confetti';
import { sendPurchaseConversion } from '../utils/adsConversion';

const ThankYouPage = () => {
  const navigate = useNavigate();
  // 2027 deep-dive Payment Links return here as /thank-you?order=<orderId>; delivery is by email, no login/dashboard
  const isDeepDive = new URLSearchParams(window.location.search).has('order');
  // Year Ahead 2027 (YearAhead2027Page) returns as /thank-you?session_id=<the session it stored before the redirect>
  const yearAheadSession = sessionStorage.getItem('yearAheadSession');
  const isYearAhead = !!yearAheadSession && new URLSearchParams(window.location.search).get('session_id') === yearAheadSession;
  const yearAheadSigns = sessionStorage.getItem('yearAheadSigns') || 'your sign';

  useEffect(() => {
    if (isYearAhead) sessionStorage.removeItem('yearAheadReturn'); // paid: /checkout no longer sends back to the order page
  }, [isYearAhead]);

  useEffect(() => {
    // Google Ads purchase conversion: only on a real return from a payment (a deep-dive order id or a
    // Checkout Session id), once per order, with the amount remembered before the redirect (utils/adsConversion).
    const q = new URLSearchParams(window.location.search);
    const transactionId = q.get('order') || q.get('session_id');
    if (transactionId) sendPurchaseConversion(transactionId);
  }, []);

  return (
    <>
      <Confetti recycle={false} numberOfPieces={200} />
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 3 }} />

          {isYearAhead ? (
            <>
              <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
                Thank you! Your Year Ahead 2027 Road Map is on its way.
              </Typography>

              <Typography variant="h6" color="text.secondary" paragraph>
                Your payment is in. Your house map and transit map for {yearAheadSigns} are emailed to the address you used at checkout, usually within minutes.
              </Typography>

              <Box sx={{ bgcolor: 'grey.50', p: 3, borderRadius: 2, my: 4, textAlign: 'left' }}>
                <Typography variant="h6" gutterBottom>
                  What happens next?
                </Typography>
                <Typography paragraph>
                  1. Watch your inbox for an email from Ivy Astrology (contact@ivyastrology.com). If it isn't there, check your spam or promotions folder.
                </Typography>
                <Typography>
                  2. Open your maps straight from the email. No login needed.
                </Typography>
              </Box>

              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/')}
                sx={{
                  px: 4,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  }
                }}
              >
                Back to Ivy Astrology
              </Button>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                Questions? Just reply to that email, or write to contact@ivyastrology.com
              </Typography>
            </>
          ) : isDeepDive ? (
            <>
              <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
                Thank you! Your 2027 Deep Dive is on its way.
              </Typography>

              <Typography variant="h6" color="text.secondary" paragraph>
                Your payment is in. Your deep-dive video and interactive maps are emailed to the address you ordered with — always within 24 hours, often within minutes.
              </Typography>

              <Box sx={{ bgcolor: 'grey.50', p: 3, borderRadius: 2, my: 4, textAlign: 'left' }}>
                <Typography variant="h6" gutterBottom>
                  What happens next?
                </Typography>
                <Typography paragraph>
                  1. Watch your inbox for an email from Ivy Astrology (contact@ivyastrology.com). If it isn't there, check your spam or promotions folder.
                </Typography>
                <Typography paragraph>
                  2. Your delivery email has your deep-dive video and your two interactive maps. Open them straight from the email. No login needed.
                </Typography>
                <Typography>
                  3. Ordered in September or October? Your 50%-off calendar code comes in your delivery email too.
                </Typography>
              </Box>

              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/')}
                sx={{
                  px: 4,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  }
                }}
              >
                Back to Ivy Astrology
              </Button>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                Questions? Just reply to that email, or write to contact@ivyastrology.com
              </Typography>
            </>
          ) : (
          <>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
            Welcome to Your 2026 Journey!
          </Typography>

          <Typography variant="h6" color="text.secondary" paragraph>
            Your purchase was successful. Check your email for access details.
          </Typography>

          <Box sx={{ bgcolor: 'grey.50', p: 3, borderRadius: 2, my: 4 }}>
            <Typography variant="h6" gutterBottom>
              What happens next?
            </Typography>
            <Typography paragraph>
              1. Check your email for login instructions
            </Typography>
            <Typography paragraph>
              2. Access your dashboard to watch your extended forecast
            </Typography>
            <Typography>
              3. Download your PDF report and birth chart wheel
            </Typography>
          </Box>

          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/dashboard')}
            sx={{
              px: 4,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
              }
            }}
          >
            Go to Dashboard
          </Button>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            Need help? Email contact@ivyastrology.com
          </Typography>
          </>
          )}
        </Paper>
      </Container>
    </>
  );
};

export default ThankYouPage;